import {ColDef, ICellRendererParams, INoRowsOverlayParams} from 'ag-grid-community';
import {CommonModule, CurrencyPipe, DatePipe} from '@angular/common';
import {TranslateModule, TranslatePipe} from '@ngx-translate/core';
import {Component, inject} from '@angular/core';
import {ICellRendererAngularComp, INoRowsOverlayAngularComp} from 'ag-grid-angular';
import {DescriptionCellRendererComponent} from './cells';
import {ConfirmationPopupService} from '@services/confirmation-popup.service';
import {BankAccountService} from '@services/bank-account.service';
import {TBankAccountSource} from '@common/types';
import {HttpErrorResponse} from '@angular/common/http';
import {NotificationService} from '@notifications';
import {useSpinner} from '@spinner';
import {TooltipDirective} from 'ngx-bootstrap/tooltip';
import {TransactionsService} from './transactions/transactions.service';
import {CdkConnectedOverlay, CdkOverlayOrigin} from '@angular/cdk/overlay';
import {MatCardModule} from '@angular/material/card';
import {CheckDataFormComponent} from './transactions/check-data-form.component';
import {ITransactionCheck} from './transactions/interface';
import {LsDatePipe} from '@common/time/ls-date.pipe';

export const getDefaultTxsColumns = (): ColDef[] => {
  const datePipe = inject(DatePipe);
  const currencyPipe = inject(CurrencyPipe);
  const translatePipe = inject(TranslatePipe);

  return [
    {
      field: 'postedDate',
      headerName: translatePipe.transform('Posted Date'),
      sort: 'desc',
      valueFormatter: params => datePipe.transform(params.value, 'MM/dd/yyyy'),
    },
    {
      field: 'bankName',
      headerName: translatePipe.transform('Bank'),
    },
    {
      field: 'bankAccount',
      headerName: translatePipe.transform('Account'),
    },
    {
      field: 'accountNumber',
      headerName: translatePipe.transform('Account Number'),
    },
    {
      field: 'description',
      headerName: translatePipe.transform('Description'),
      cellRenderer: DescriptionCellRendererComponent,
    },
    {
      field: 'userComment',
      headerName: 'Client Comment',
    },
    {
      field: 'amount',
      headerName: translatePipe.transform('Amount'),
      type: 'rightAligned',
      valueFormatter: params => currencyPipe.transform(params.value),
      cellClass: 'text-end',
    },
    {
      field: 'payee',
      headerName: translatePipe.transform('Payee'),
      initialHide: true,
    },
    {
      field: 'exportedDate',
      headerName: translatePipe.transform('Exported Date'),
      valueFormatter: params => datePipe.transform(params.value, 'MM/dd/yyyy'),
      initialHide: true,
    },
    {
      field: 'syncedDate',
      headerName: translatePipe.transform('Synced Date'),
      valueFormatter: params => datePipe.transform(params.value, 'MM/dd/yyyy'),
    },
    {
      field: 'transactionDate',
      headerName: translatePipe.transform('Transaction Date'),
      initialHide: true,
      valueFormatter: params => datePipe.transform(params.value, 'MM/dd/yyyy'),
    },
  ];
}

@Component({
  selector: 'app-loading-overlay',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
  ],
  template: `
      <h4 class="empty-message">
          {{ 'No Transactions Found'|translate }}
      </h4>
  `,
  styles: [`
    .empty-message {
      color: #777777;
    }
  `],
})
export class NoRowsOverlayComponent implements INoRowsOverlayAngularComp {
  params: INoRowsOverlayParams;

  agInit(params: INoRowsOverlayParams): void {
    this.params = params;
  }
}

export class Socket {
  socket: WebSocket;

  constructor(url: string) {
    this.socket = new WebSocket(url);
  }

  disconnect() {
    if (this.socket) {
      this.socket.close();
    }
  }

  on(eventName: string, callback: EventListenerOrEventListenerObject) {
    if (this.socket) {
      this.socket.addEventListener(eventName, callback);
    }
  }
}

export function checkAccountDeleteStatus(
  error: HttpErrorResponse,
  bankAccountService: BankAccountService,
  confirmationPopupService: ConfirmationPopupService,
  notificationService: NotificationService,
  accountId: number,
  callback: () => void,
) {
  if (error.status === 428) {
    confirmationPopupService.confirm({
      text: notificationService.extractErrorMessage(error),
      yesCaption: 'Proceed',
      noCaption: 'Cancel',
      yes: () => {
        bankAccountService.deleteAccount(accountId, true)
        .pipe(useSpinner())
        .subscribe({
          next: () => {
            notificationService.success('Account Deleted');
            callback?.();
          },
          error: (error: HttpErrorResponse) => checkAccountDeleteStatus(
            error,
            bankAccountService,
            confirmationPopupService,
            notificationService,
            accountId,
            callback,
          ),
        });
      },
    });
  } else {
    notificationService.error(error);
  }
}

@Component({
  standalone: true,
  template: `
      @if (params.value) {
          <span
                  class="fa-regular fa-rectangle-list link-primary cursor-pointer fs-16"
                  tooltip="Show Check Image"
                  placement="left"
                  boundariesElement="viewport"
                  container="body"
                  containerClass="me-2"
                  cdkOverlayOrigin
                  #trigger="cdkOverlayOrigin"
                  (click)="$event.stopPropagation();showCheckImage();"
          ></span>
          <ng-template
                  cdkConnectedOverlay
                  cdkConnectedOverlayPanelClass="mat-elevation-z4"
                  [cdkConnectedOverlayOffsetY]="-16"
                  [cdkConnectedOverlayOrigin]="trigger"
                  [cdkConnectedOverlayOpen]="opened"
                  (overlayOutsideClick)="onOverlayOutsideClick($event)"
          >

              <div class="bg-white p-3">
                <div class="d-flex align-items-center gap-2 justify-content-between">
                    <div class="text-black fs-5">
                        {{ 'Check'|translate }} #{{ checkData.ocrCheckNumber }}
                        from {{ checkData.ocrCheckDate|lsDate }} for {{ checkData.ocrAmount|currency }}
                    </div>
                    <button class="btn-close" (click)="opened = false;"></button>
                </div>
                <div class="d-flex gap-2">
                    @if (opened && checkData) {
                        @if (checkData.storageUri) {
                            <div class="check-image-container">
                                <img [src]="checkData.storageUri" alt="Check Image" class="check-image"/>
                            </div>
                        }
                        <app-check-data-form
                                [checkData]="checkData"
                                (updated)="onCheckUpdated($event)"
                        ></app-check-data-form>
                    }
                </div>
              </div>
          </ng-template>
      }
  `,
  styles: [`
    .check-image-content {
      width: 500px;
    }

    .check-image-container {
      display: flex;
      justify-content: center;
    }

    .check-image {
      max-width: 700px;
      height: auto;
    }
    
    .fs-16 {
      font-size: 16px;
    }
  `],
  imports: [
    TooltipDirective,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    MatCardModule,
    TranslateModule,
    CheckDataFormComponent,
    CommonModule,
    LsDatePipe,
  ]
})
export class CheckCellRendererComponent<T> implements ICellRendererAngularComp {
  params: ICellRendererParams<T>;
  opened: boolean = false;
  checkData: ITransactionCheck;

  private transactionsService: TransactionsService = inject(TransactionsService);
  private notificationService: NotificationService = inject(NotificationService);

  refresh(): boolean {
    return false;
  }

  agInit(params: ICellRendererParams<T>): void {
    this.params = params;
  }

  showCheckImage() {
    this.transactionsService.getCheck(this.params.value)
    .pipe(useSpinner())
    .subscribe({
      next: (check) => {
        this.checkData = check;
        this.opened = true;
      },
      error: (error) => this.notificationService.error(error),
    });
  }

  onCheckUpdated(updatedCheck: ITransactionCheck) {
    this.checkData = updatedCheck;
  }

  onOverlayOutsideClick(event: MouseEvent) {
    // Check if the click is on the date picker popup
    // ngx-bootstrap datepicker appends the popup to body, so we need to check for it
    const target = event.target as HTMLElement;
    
    // Check if the click target or any parent element is part of the date picker
    // The date picker popup has class 'bs-datepicker' or contains 'bs-datepicker' in class name
    const isDatePickerClick = target.closest('.bs-datepicker') !== null ||
                              target.closest('[class*="bs-datepicker"]') !== null;
    
    // Don't close the overlay if clicking on the date picker
    if (!isDatePickerClick) {
      this.opened = false;
    }
  }
}
